import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import TimerIcon from '@mui/icons-material/Timer'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import { getOverview } from 'services/portfolioService'
import ActionsAPI from 'services/ActionsAPI'
import { useNavigate } from 'react-router-dom'
import { PRIVATE_PATHS } from 'utils/paths'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import { selectComplianceEnabled } from 'features/auth/ducks/selectors'
import AssetFilesDialog from './AssetFilesDialog'
import FileTypeFilesDialog from './FileTypeFilesDialog'

const ASSET_TYPE_LABELS = {
  lift: 'Lift / Elevator',
  generator: 'Generator',
  boiler: 'Boiler',
  pressure_system: 'Pressure System',
  fire_system: 'Fire System',
  hvac: 'HVAC / Ventilation',
  water_system: 'Water System',
}

const OverviewTab = ({ portfolioId, anyBpHasTaskManagement = false }) => {
  const complianceEnabled = useSelector(selectComplianceEnabled)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [insightFile, setInsightFile] = useState(null)
  const [assetFilesTarget, setAssetFilesTarget] = useState(null)
  const [fileTypeTarget, setFileTypeTarget] = useState(null)
  const [actionStats, setActionStats] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!portfolioId) return
    setLoading(true)
    getOverview(portfolioId)
      .then(res => setData(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [portfolioId])

  useEffect(() => {
    if (!portfolioId || !anyBpHasTaskManagement) return
    let cancelled = false
    ActionsAPI.stats({ portfolio_id: portfolioId })
      .then(res => { if (!cancelled) setActionStats(res?.data?.data || null) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [portfolioId, anyBpHasTaskManagement])

  if (loading) return <LoadingState><CircularProgress size={28} sx={{ color: '#D24724' }} /></LoadingState>
  if (!data) return <EmptyMsg>Failed to load overview.</EmptyMsg>

  const goToBp = (bpId) => navigate(PRIVATE_PATHS.bpDetails.replace(':id', bpId))
  // Asset click should land on the BP's documents/files section, not the default folder.
  const goToBpFiles = (bpId) =>
    navigate(`${PRIVATE_PATHS.bpList}/${bpId}/files`)

  const openInsight = (fileId, displayName, bpId, bpName) => {
    setInsightFile({ id: fileId, displayName, buildingPassportId: bpId, buildingPassportName: bpName })
  }

  return (
    <Container>
      <StatsGrid>
        <StatCard>
          <StatIcon $color="#D24724"><BusinessIcon style={{ fontSize: 20 }} /></StatIcon>
          <StatInfo><StatValue>{data.buildingCount}</StatValue><StatLabel>Building Passports</StatLabel></StatInfo>
        </StatCard>
        <StatCard>
          <StatIcon $color="#33becc"><InsertDriveFileIcon style={{ fontSize: 20 }} /></StatIcon>
          <StatInfo><StatValue>{data.fileCount}</StatValue><StatLabel>Total Documents</StatLabel></StatInfo>
        </StatCard>
        <StatCard title="Renewable documents past their expiry date, not yet renewed">
          <StatIcon $color="#dc2626"><WarningAmberIcon style={{ fontSize: 20 }} /></StatIcon>
          <StatInfo><StatValue>{data.expiredCount}</StatValue><StatLabel>Overdue for Renewal</StatLabel></StatInfo>
        </StatCard>
        <StatCard title="Documents expiring within the next 90 days">
          <StatIcon $color="#f59e0b"><TimerIcon style={{ fontSize: 20 }} /></StatIcon>
          <StatInfo><StatValue>{data.expiringSoonCount}</StatValue><StatLabel>Due Within 90 Days</StatLabel></StatInfo>
        </StatCard>
        <StatCard title="Documents that expired but have been replaced by a newer version">
          <StatIcon $color="#059669"><SwapHorizIcon style={{ fontSize: 20 }} /></StatIcon>
          <StatInfo><StatValue>{data.renewedCount}</StatValue><StatLabel>Successfully Renewed</StatLabel></StatInfo>
        </StatCard>
        {anyBpHasTaskManagement && (
          <StatCard
            title="Open recommendations awaiting triage across this portfolio"
            onClick={() => navigate(`${PRIVATE_PATHS.portfolioDetail.replace(':id', portfolioId)}?tab=actions&status=open`)}
            style={{ cursor: 'pointer' }}
          >
            <StatIcon $color="#33becc"><PendingActionsIcon style={{ fontSize: 20 }} /></StatIcon>
            <StatInfo>
              <StatValue>{actionStats?.byStatus?.open ?? 0}</StatValue>
              <StatLabel>Open Actions{(actionStats?.overdueOpen ?? 0) > 0 ? ` · ${actionStats.overdueOpen} overdue` : ''}</StatLabel>
            </StatInfo>
          </StatCard>
        )}
      </StatsGrid>

      {data.buildings?.length > 0 && (
        <Section>
          <SectionHead>Building Passports</SectionHead>
          <BuildingTable>
            <thead>
              <tr><Th>Building Passport</Th><Th $right>Documents</Th><Th $right>Overdue</Th>{complianceEnabled && <Th>Compliance</Th>}</tr>
            </thead>
            <tbody>
              {data.buildings.map(bp => (
                <Tr key={bp.id} onClick={() => goToBp(bp.id)}>
                  <Td>
                    <BpRow>
                      <BpRowText>
                        <BpNameLink>{bp.name} <OpenInNewIcon style={{ fontSize: 12, opacity: 0.4 }} /></BpNameLink>
                        {bp.street && <BpAddr>{bp.street}, {bp.city}</BpAddr>}
                      </BpRowText>
                    </BpRow>
                  </Td>
                  <Td $right>{bp.fileCount}</Td>
                  <Td $right>{bp.expiredCount > 0 ? <WarnText>{bp.expiredCount}</WarnText> : '0'}</Td>
                  {complianceEnabled && (
                    <Td>
                      {bp.expiredCount === 0 ? (
                        <StatusBadge $color="#059669" $bg="#ecfdf5"><CheckCircleOutlineIcon style={{ fontSize: 14 }} /> Up to date</StatusBadge>
                      ) : (
                        <StatusBadge $color="#dc2626" $bg="#fef2f2"><WarningAmberIcon style={{ fontSize: 14 }} /> {bp.expiredCount} overdue</StatusBadge>
                      )}
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </BuildingTable>
        </Section>
      )}

      {data.upcomingEvents?.length > 0 && (
        <Section>
          <SectionHead>Next Upcoming Renewals</SectionHead>
          <EventList>
            {data.upcomingEvents.map((evt, i) => (
              <EventRow key={`${evt.fileId}-${evt.eventDate || i}`} onClick={() => openInsight(evt.fileId, evt.displayName, evt.buildingPassportId, evt.buildingPassportName)}>
                <EventDate $urgent={evt.daysUntil <= 30}>
                  {evt.eventDate}
                  <EventDays>{evt.daysUntil <= 0 ? 'overdue' : `in ${evt.daysUntil} days`}</EventDays>
                </EventDate>
                <EventInfo>
                  <EventName>{evt.displayName}</EventName>
                  <EventMeta>{evt.fileType}: {evt.buildingPassportName}</EventMeta>
                </EventInfo>
              </EventRow>
            ))}
          </EventList>
        </Section>
      )}

      {data.fileTypeDistribution?.length > 0 && (
        <Section>
          <SectionHead>Document Types</SectionHead>
          <TypeList>
            {data.fileTypeDistribution.map(ft => (
              <TypeRow key={ft.fileTypeId} onClick={() => setFileTypeTarget(ft)}>
                <TypeName>{ft.name}</TypeName>
                <TypeCount>{ft.count} file{ft.count !== 1 ? 's' : ''} <OpenInNewIcon style={{ fontSize: 11, opacity: 0.3 }} /></TypeCount>
              </TypeRow>
            ))}
          </TypeList>
        </Section>
      )}

      {data.assets?.length > 0 && (
        <Section>
          <SectionHead><PrecisionManufacturingIcon style={{ fontSize: 16 }} /> Building Assets ({data.assets.length})</SectionHead>
          <AssetList>
            {data.assets.map(asset => (
              <AssetRow key={asset.id} onClick={() => setAssetFilesTarget(asset)}>
                <AssetInfo>
                  <AssetName>{asset.name}</AssetName>
                  <AssetMeta>
                    {ASSET_TYPE_LABELS[asset.assetType] || asset.assetType}
                    {asset.location && `: ${asset.location}`}
                    {'. '}{asset.buildingPassportName}
                  </AssetMeta>
                </AssetInfo>
                <AssetFiles>{asset.fileCount} doc{asset.fileCount !== 1 ? 's' : ''} <OpenInNewIcon style={{ fontSize: 11, opacity: 0.3 }} /></AssetFiles>
              </AssetRow>
            ))}
          </AssetList>
        </Section>
      )}

      {insightFile && (
        <GuruInsightModal
          open
          onClose={() => setInsightFile(null)}
          file={insightFile}
          bpId={insightFile.buildingPassportId}
          buildingPassportName={insightFile.buildingPassportName}
        />
      )}

      <AssetFilesDialog
        asset={assetFilesTarget}
        open={!!assetFilesTarget}
        onClose={() => setAssetFilesTarget(null)}
      />

      <FileTypeFilesDialog
        portfolioId={portfolioId}
        fileType={fileTypeTarget}
        open={!!fileTypeTarget}
        onClose={() => setFileTypeTarget(null)}
      />
    </Container>
  )
}

export default OverviewTab

const Container = styled.div``
const LoadingState = styled.div` display: flex; justify-content: center; padding: 48px; `
const EmptyMsg = styled.div` text-align: center; padding: 48px; color: #999; font-size: 14px; `

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
  @media (max-width: 480px) { grid-template-columns: repeat(2, 1fr); }
`

const StatCard = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 16px;
  background: white; border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px;
`

const StatIcon = styled.div`
  width: 40px; height: 40px; border-radius: 8px;
  background: ${({ $color }) => $color}14; color: ${({ $color }) => $color};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`

const StatInfo = styled.div``
const StatValue = styled.div` font-size: 22px; font-weight: 700; color: ${({ theme }) => theme.colors.haiti}; line-height: 1; `
const StatLabel = styled.div` font-size: 12px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin-top: 2px; `

const Section = styled.div` margin-bottom: 24px; `
const SectionHead = styled.h3`
  font-size: 15px; font-weight: 600; color: ${({ theme }) => theme.colors.haiti}; margin: 0 0 12px;
  display: flex; align-items: center; gap: 6px;
  &:before { content: ''; display: inline-block; width: 3px; height: 14px; background: ${({ theme }) => theme.colors.primary}; border-radius: 2px; }
`

const BuildingTable = styled.table`
  width: 100%; border-collapse: collapse; background: white;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px; overflow: hidden;
`

const Th = styled.th`
  padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; text-align: ${({ $right }) => $right ? 'right' : 'left'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; background: ${({ theme }) => theme.colors.grey?.[400] || '#fafafa'};
`

const Tr = styled.tr`
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.grey?.[400] || '#fafafa'}; }
  &:not(:last-child) td { border-bottom: 1px solid #f2f2f2; }
`

const Td = styled.td`
  padding: 12px 14px; font-size: 13px; color: ${({ theme }) => theme.colors.haiti};
  text-align: ${({ $right }) => $right ? 'right' : 'left'};
`

const BpRow = styled.div` display: flex; align-items: center; gap: 12px; `
const BpRowText = styled.div` min-width: 0; flex: 1; `
const BpNameLink = styled.div` font-weight: 500; color: ${({ theme }) => theme.colors.primary}; `
const BpAddr = styled.div` font-size: 12px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin-top: 1px; `
const WarnText = styled.span` color: #dc2626; font-weight: 600; `

const StatusBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600;
  padding: 3px 8px; border-radius: 4px; color: ${({ $color }) => $color}; background: ${({ $bg }) => $bg};
`

const EventList = styled.div` display: flex; flex-direction: column; gap: 8px; `
const EventRow = styled.div`
  display: flex; gap: 14px; padding: 12px 14px; background: white; cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}40; }
`

const EventDate = styled.div`
  font-size: 12px; font-weight: 600; white-space: nowrap; min-width: 100px;
  color: ${({ $urgent, theme }) => $urgent ? '#dc2626' : theme.colors.haiti};
`

const EventDays = styled.span` display: block; font-size: 11px; font-weight: 400; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; `
const EventInfo = styled.div` min-width: 0; flex: 1; `
const EventName = styled.div` font-size: 13px; font-weight: 500; color: ${({ theme }) => theme.colors.primary}; `
const EventMeta = styled.div` font-size: 12px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin-top: 2px; `

const TypeList = styled.div`
  background: white; border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px; overflow: hidden;
`

const TypeRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; font-size: 13px; cursor: pointer;
  &:not(:last-child) { border-bottom: 1px solid #f2f2f2; }
  &:hover { background: ${({ theme }) => theme.colors.grey?.[400] || '#fafafa'}; }
`

const TypeName = styled.span` color: ${({ theme }) => theme.colors.haiti}; `
const TypeCount = styled.span` display: flex; align-items: center; gap: 4px; font-weight: 500; color: ${({ theme }) => theme.colors.primary}; white-space: nowrap; `

const AssetList = styled.div`
  background: white; border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'}; border-radius: 8px; overflow: hidden;
`

const AssetRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; padding: 12px 14px; cursor: pointer;
  &:not(:last-child) { border-bottom: 1px solid #f2f2f2; }
  &:hover { background: ${({ theme }) => theme.colors.grey?.[400] || '#fafafa'}; }
`

const AssetInfo = styled.div` min-width: 0; `
const AssetName = styled.div` font-size: 13px; font-weight: 500; color: ${({ theme }) => theme.colors.haiti}; `
const AssetMeta = styled.div` font-size: 11px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin-top: 2px; `
const AssetFiles = styled.span` display: flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 500; color: ${({ theme }) => theme.colors.primary}; white-space: nowrap; `
